import React, { useRef } from 'react';
import AssetWrapper, { AssetWrapperProps } from 'src/app/atoms/Asset/AssetWrapper';
import Stack from 'src/app/atoms/Layout/Stack/Stack';
import Typography, { TypographyProps } from 'src/app/atoms/Typography/Typography';
import { PlayButtonContainer, TestimonialTextBlock, VideoContainer } from './TestimonialCard.styles';
import useOnHover from 'src/hooks/useOnHover';
import Clickable from 'src/app/atoms/Clickable/Clickable';
import PlayButtonIcon from '../PlayButtonIcon/PlayButtonIcon';
import { SpacingPropKeys } from 'src/app/styles/spacing';
import useOnDrag from 'src/hooks/useOnDrag';

export interface TestimonialCardProps {
  testimonialTitle: TypographyProps;
  testimonialText?: TypographyProps;
  testimonialTextGap?: SpacingPropKeys;
  image: AssetWrapperProps['imageProps'];
  imageMaxWidth?: string | number;
  posterVideo?: AssetWrapperProps['videoProps'];
  onClick?: () => void;
  playIconOnHover?: boolean;
}

const TestimonialCard = ({
  testimonialTitle,
  testimonialText,
  testimonialTextGap = 'spacing-3xl',
  image,
  imageMaxWidth,
  posterVideo,
  onClick,
  playIconOnHover,
}: TestimonialCardProps): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);
  const isHovering = useOnHover(ref);
  const isDragging = useOnDrag(ref);

  const isPlayingPreview = (isHovering || isDragging) && posterVideo;

  const clickable = !!onClick;

  const renderContent = () => (
    <Stack
      borderRadius='radius-2xl'
      overflow='hidden'
      position='relative'
      className='testimonial-card-container'
      ref={ref}
    >
      <AssetWrapper
        imageProps={image}
        maxWidth={imageMaxWidth}
      />
      {isPlayingPreview && (
        <VideoContainer>
          <AssetWrapper
            cover
            videoProps={{ autoPlay: true, loop: true, muted: true, playsInline: true, ...posterVideo }}
          />
          {playIconOnHover && (
            <PlayButtonContainer>
              <PlayButtonIcon />
            </PlayButtonContainer>
          )}
        </VideoContainer>
      )}
      <TestimonialTextBlock
        position='absolute'
        padding='spacing-xl'
      >
        <Stack
          borderRadius='radius-2xl'
          background='bg-primary'
          padding='spacing-xl'
          className='testimonial-text-block-container'
          direction='column'
          gap={testimonialTextGap}
        >
          <Typography {...testimonialTitle} />
          {testimonialText && <Typography {...testimonialText} />}
        </Stack>
      </TestimonialTextBlock>
    </Stack>
  );

  if (clickable) {
    return <Clickable onClick={onClick}>{renderContent()}</Clickable>;
  }

  return renderContent();
};

export default TestimonialCard;
